import { AliasType, MediaEventCoverageOption, Provider } from '../gql/graphql';
import {
  CoverageDetails,
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  PaginatedEdges,
  PaginatedEdgesWithPageInfo,
  ParticipantCoverageDetails,
  RaterParticipantCoverageDetails,
  UnderwritingRatingId,
} from './types';
import { CoverageUuid, EndorsementUuid } from '@converge/data';
import {
  QuoteLineItemFeatures,
  QuoteLineItemType,
} from './utils.quote-line-item-features';
import { isNotNullOrUndefined, limitNumberToTwoDigits } from '@converge/utils';

import { config } from '@converge/config';
import snakeCase from 'lodash.snakecase';
import { useEffect, useState } from 'react';

export const toPagination = (pageNumber = 0, pageSize = 50) => {
  return {
    after: pageSize * pageNumber,
    first: pageSize,
  };
};

export const getFirstPaginatedNode = <Data = unknown>(
  data: PaginatedEdges<Data> | undefined
): Data | undefined => {
  return data?.edges?.[0]?.node;
};

export const findPaginatedNode = <Data = unknown>(
  data: PaginatedEdges<Data> | undefined,
  find: (data: Data) => boolean
): Data | undefined => {
  return data?.edges.find((data) => find(data.node))?.node;
};

export const mapPaginatedNodes = <Data = unknown, T = unknown>(
  data: Partial<PaginatedEdges<Data>> | undefined,
  map: (data: Data) => T
): T[] => {
  return data?.edges?.map((data) => map(data.node)) ?? [];
};

export const getPaginatedNodes = <Data = unknown>(
  data: Partial<PaginatedEdges<Data>> | undefined
): Data[] => {
  return mapPaginatedNodes(data, (data) => data);
};

export const getPaginationTotalResults = (
  data: PaginatedEdgesWithPageInfo | undefined
): number | undefined => {
  return data?.pageInfo.totalResults;
};

export const sortCoveragesByType = <
  T extends
    | IUnderwritingCoverage
    | CoverageDetails
    | RaterParticipantCoverageDetails
    | ParticipantCoverageDetails
>(
  coverages: T[]
): T[] => {
  return coverages.sort((coverageA, coverageB) => {
    const typeSort =
      coverageTypeToOrder(coverageA.type) - coverageTypeToOrder(coverageB.type);

    if (typeSort === 0) {
      return (coverageA.order ?? 0) - (coverageB.order ?? 0);
    }

    return typeSort;
  }) as T[];
};

export const coverageTypeToOrder = (type: string | null | undefined) => {
  switch (type) {
    case 'liability':
      return 0;
    case 'first_party':
      return 1;
    default:
      return 2;
  }
};

export const addMetadataToCoverage = ({
  ...coverage
}: IUnderwritingCoverage) => {
  coverage.features = QuoteLineItemFeatures.from(
    QuoteLineItemType.Coverage,
    coverage
  ).toJSON();

  coverage.slug =
    coverage.slug ?? (snakeCase(coverage.name ?? '') as UnderwritingRatingId);

  // coverage.enableRetention = true;

  // coverage.enableWaitingPeriodHours = features.enableWaitingPeriodHours;

  // coverage.enableWaitingPeriodDays = features.enableWaitingPeriodDays;

  // coverage.enableRestorationPeriodDays = features.enableRestorationPeriodDays;

  // coverage.showRetroactiveDateFields =
  //   features.enableRetroactiveDateOrFullPriorActs;

  const retroactiveDate = coverage.features.enableRetroactiveDateOrFullPriorActs
    ? coverage.quote.fullPriorActs
      ? undefined
      : coverage.quote?.retroactiveDate ?? undefined
    : undefined;

  coverage.quote = {
    ...coverage.quote,
    waitingPeriodDays: coverage.features.enableWaitingPeriodDays
      ? coverage.quote?.waitingPeriodDays ?? config.defaults.waitingPeriodDays
      : undefined,
    waitingPeriodHours: coverage.features.enableWaitingPeriodHours
      ? coverage.quote?.waitingPeriodHours ?? config.defaults.waitingPeriodHours
      : undefined,
    restorationPeriodDays: coverage.features.enableRestorationPeriodDays
      ? coverage.quote?.restorationPeriodDays ??
        config.defaults.restorationPeriodDays
      : undefined,
    fullPriorActs: coverage.features.enableRetroactiveDateOrFullPriorActs
      ? retroactiveDate
        ? false
        : true
      : undefined,
    retroactiveDate,
  };

  if (coverage.quote.fusionScoreModifier) {
    coverage.quote.fusionScoreModifier = limitNumberToTwoDigits(
      coverage.quote.fusionScoreModifier
    );
  }

  coverage.subCoverages = [
    ...coverage.subCoverages.map((subCoverage) => {
      const newSubCoverage: typeof subCoverage = {
        ...subCoverage,
        features: QuoteLineItemFeatures.from(
          QuoteLineItemType.SubCoverage,
          subCoverage
        ).toJSON(),
        quote: {
          ...subCoverage.quote,
        },
      };

      if (!newSubCoverage.features.enableRetention) {
        newSubCoverage.quote.subRetention = undefined;
      }

      newSubCoverage.quote = {
        ...newSubCoverage.quote,
        waitingPeriodHours: newSubCoverage.features.enableWaitingPeriodHours
          ? newSubCoverage.quote?.waitingPeriodHours ??
            config.defaults.waitingPeriodHours
          : undefined,
        restorationPeriodDays: newSubCoverage.features
          .enableRestorationPeriodDays
          ? newSubCoverage.quote?.restorationPeriodDays ??
            config.defaults.restorationPeriodDays
          : undefined,
      };

      return newSubCoverage;
    }),
  ];

  return coverage;
};

export const addMetadataToEndorsements = ({
  ...endorsement
}: IUnderwritingEndorsement): IUnderwritingEndorsement => {
  endorsement.features = QuoteLineItemFeatures.from(
    QuoteLineItemType.Endorsement,
    endorsement
  ).toJSON();

  if (endorsement.features.enableMediaEventCoverage) {
    endorsement.quote = {
      ...endorsement.quote,
      mediaEventCoverageOption: endorsement.features.enableMediaEventCoverage
        ? endorsement.quote.mediaEventCoverageOption ??
          MediaEventCoverageOption.Standard
        : null,
    };
  }

  if (endorsement.quote.fullPriorActs && endorsement.quote.retroactiveDate) {
    endorsement.quote = {
      ...endorsement.quote,
      retroactiveDate: undefined,
    };
  }

  endorsement.questions =
    endorsement.questions ?? ({ edges: [] } as typeof endorsement.questions);

  endorsement.questions.edges = endorsement.questions.edges.map((edge) => {
    return {
      ...edge,
      node: {
        ...edge.node,
        parent: `${edge.node.parent}` === 'true',
      },
    };
  });

  return endorsement;
};

export const setDefaultRetractiveDateToCoverage = ({
  coverage,
  isDefaultQuote,
  submissionRetroactiveDate,
}: {
  coverage: IUnderwritingCoverage;
  isDefaultQuote: boolean;
  submissionRetroactiveDate: string | null | undefined;
}) => {
  if (
    coverage.features.enableRetroactiveDateOrFullPriorActs &&
    isDefaultQuote &&
    submissionRetroactiveDate
  ) {
    // coverage.quote.retroactiveDate = submissionRetroactiveDate;
  }

  return coverage;
};

export const setDefaultRetractiveDateToEndorsement = ({
  endorsement,
  isDefaultQuote,
  submissionRetroactiveDate,
}: {
  endorsement: IUnderwritingEndorsement;
  isDefaultQuote: boolean;
  submissionRetroactiveDate: string | null | undefined;
}) => {
  if (
    endorsement?.features?.enableRetroactiveDateOrFullPriorActs &&
    isDefaultQuote &&
    submissionRetroactiveDate
  ) {
    endorsement.quote.retroactiveDate = submissionRetroactiveDate;
  }

  return endorsement;
};

const PROVIDER_SOURCE_ORDERING = {
  [Provider.Primary.toString()]: 1,
  [Provider.Converge.toString()]: 2,
  [Provider.Dnb.toString()]: 3,
  [Provider.SecurityScorecard.toString()]: 4,
  [Provider.Constella.toString()]: 5,
  [Provider.Relativity6.toString()]: 6,
};

export const getProviderSourceOrdering = (provider?: Provider | null) => {
  if (!provider) {
    return 9999;
  }
  return PROVIDER_SOURCE_ORDERING[provider?.toString()] ?? 9999;
};

export const sortProviders = (providers: Provider[]) => {
  return [...providers].sort((providerA, providerB) => {
    return (
      (PROVIDER_SOURCE_ORDERING[providerA.toString()] ?? 9999) -
      (PROVIDER_SOURCE_ORDERING[providerB.toString()] ?? 9999)
    );
  });
};

export const getMostImportantProvider = (data: {
  [key: string]: unknown;
  source?: Provider | null;
  sources?: Provider[] | null;
}) => {
  return data.sources?.length ? sortProviders(data.sources)[0] : data.source;
};

export const sortSubmissionDetailsBySource = <
  T extends {
    [key: string]: unknown;
    source?: Provider | null;
    sources?: Provider[] | null;
  }
>(
  data: T[]
): T[] => {
  return data.sort((datumA, datumB) => {
    return (
      getProviderSourceOrdering(getMostImportantProvider(datumA)) -
      getProviderSourceOrdering(getMostImportantProvider(datumB))
    );
  });
};

export const providerToDisplay = (
  provider: Provider | string | null | undefined
) => {
  switch (provider) {
    case Provider.Primary:
      return 'Primary';
    case Provider.Converge:
      return 'Converge';
    case Provider.Relativity6:
      return 'Relativity6';
    case Provider.Constella:
      return 'Constella';
    case Provider.Dnb:
      return 'Dun & Bradstreet';
    case Provider.SecurityScorecard:
      return 'SecurityScorecard';
    default:
      return provider ?? 'N/A';
  }
};

export const withoutPrimaryProvider = (
  providers: (Provider | string | null | undefined)[] | undefined | null
) => {
  return (providers ?? []).filter(
    (provider) =>
      isNotNullOrUndefined(provider) && provider !== Provider.Primary
  );
};

export const providerListWithoutPrimaryToDisplay = (
  providers: (Provider | string | null | undefined)[] | undefined | null
) => {
  return withoutPrimaryProvider(providers)
    .sort(
      (providerA, providerB) =>
        (PROVIDER_SOURCE_ORDERING[providerA?.toString() ?? ''] ?? -1) -
        (PROVIDER_SOURCE_ORDERING[providerB?.toString() ?? ''] ?? -1)
    )
    .map(providerToDisplay);
};

export const canCoverageHaveRetroactiveDate = (uuid: CoverageUuid) => {
  return [
    CoverageUuid.NetworkSecurityPrivacyLiability,
    CoverageUuid.RegulatoryProceedings,
    CoverageUuid.PciDssEvents,
  ].includes(uuid);
};

export const canEndorsementHaveRetroactiveDate = (
  endorsementUuid: EndorsementUuid
) => {
  return [
    EndorsementUuid.MediaEventLiability,
    EndorsementUuid.TechnologyErrorsAndOmissionsEndorsement,
    // EndorsementUuid.MiscellaneousProfessionalLiabilityErrorsAndOmissions,
  ].includes(endorsementUuid);
};

export type EntityAliasBasicDetails = {
  type?: string | null;
  sources?: Provider[] | null;
  name?: string | null;
};

export type EntityForAliasFind = {
  aliases?: {
    edges: {
      node: EntityAliasBasicDetails;
    }[];
  };
};

export const getDisplayNameAliasFromEntity = (
  entity?: EntityForAliasFind,
  type = AliasType.LegalName,
  source = Provider.Primary,
  fallbackSource = Provider.Converge
): EntityAliasBasicDetails | undefined => {
  return (
    entity?.aliases?.edges?.find(
      (alias) =>
        alias?.node?.sources?.includes(source) && alias?.node.type === type
    )?.node ??
    entity?.aliases?.edges?.find(
      (alias) =>
        alias?.node?.sources?.includes(fallbackSource) &&
        alias?.node?.type === type
    )?.node ??
    entity?.aliases?.edges?.find((alias) => alias?.node.type === type)?.node ??
    entity?.aliases?.edges?.find((alias) =>
      alias?.node?.sources?.includes(source)
    )?.node ??
    getFirstPaginatedNode(entity?.aliases)
  );
};

export const getDisplayNameFromEntity = (
  entity?: EntityForAliasFind,
  type = AliasType.LegalName,
  source = Provider.Primary,
  fallbackSource = Provider.Converge
): string | null | undefined => {
  return getDisplayNameAliasFromEntity(entity, type, source, fallbackSource)
    ?.name;
};

export const hasTokenExpired = (expiresEpoch: number) => {
  const secondsUntilEpoch = Date.now() / 1000;
  const dif = expiresEpoch - secondsUntilEpoch;
  return dif < 300;
};

export const useDebounce = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
